import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Slider,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
} from "@mui/material";
import { FaDownload } from "react-icons/fa";
import ChartSummaryRows from "./ChartSummaryRows";
import {
  getLocalDateOrFallback,
  getLocalHourOrFallback,
} from "../utils/localDateTime";
import { RISK_CHART_COLORS } from "../../utils/actionCardColors";

// ─── shared UI pieces ──────────────────────────────────────────────────────

const MultiSelect = ({ label, options, value, onChange, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <TextField
      select
      label={label}
      SelectProps={{
        multiple: true,
        open,
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false),
        renderValue: (selected) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {selected.map((v) => (
              <Chip
                key={v}
                label={v}
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onDelete={() => onDelete(v)}
              />
            ))}
          </Box>
        ),
      }}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        setOpen(false);
      }}
      fullWidth
      size="small"
      sx={{ mt: 1 }}
    >
      {options.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {opt}
        </MenuItem>
      ))}
    </TextField>
  );
};

// ─── helpers ────────────────────────────────────────────────────────────────

const MIN_DATE = "2017-01-01";

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const toDateStr = (d) => d.toISOString().slice(0, 10);
const clampFromMin = (dateStr) => (dateStr && dateStr < MIN_DATE ? MIN_DATE : dateStr);

const defaultDateFrom = () => clampFromMin(toDateStr(new Date(Date.now() - ONE_WEEK_MS)));
const defaultDateTo = () => toDateStr(new Date());

const parseDateStrUTC = (dateStr) => {
  const [y, m, d] = String(dateStr).split("-").map((n) => Number(n));
  return new Date(Date.UTC(y, m - 1, d));
};

const formatDateStrUTC = (dateObj) => {
  const y = dateObj.getUTCFullYear();
  const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDateRangeStringsInclusive = (fromStr, toStr) => {
  const safeFrom = clampFromMin(fromStr || MIN_DATE);
  const safeTo = toStr || toDateStr(new Date());
  const startStr = safeFrom <= safeTo ? safeFrom : safeTo;
  const endStr = safeFrom <= safeTo ? safeTo : safeFrom;
  const start = parseDateStrUTC(startStr);
  const end = parseDateStrUTC(endStr);
  const out = [];
  const cur = new Date(start);
  while (cur <= end) {
    out.push(formatDateStrUTC(cur));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out.length ? out : [safeFrom];
};

const fmtDateLabel = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = d
    .toLocaleString("en-GB", { month: "short", timeZone: "UTC" })
    .replace(".", "")
    .toUpperCase();
  const year = d.getUTCFullYear();
  return `${day}-${month}-${year}`.toUpperCase();
};

const downloadCSV = (records) => {
  if (!records.length) return;
  const headers = Object.keys(records[0]);
  const rows = [
    headers.join(","),
    ...records.map((r) =>
      headers
        .map((h) => {
          const v = r[h] ?? "";
          const s = String(v).replace(/"/g, '""');
          return s.includes(",") || s.includes("\n") ? `"${s}"` : s;
        })
        .join(",")
    ),
  ];
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "mission_data.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Custom Tooltip ──────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label, showGreen, showYellow, showRed }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        p: 1.5,
        fontSize: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
      }}
    >
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
        {label}
      </Typography>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.fill }}>
          {p.name}: {p.value}
        </div>
      ))}
    </Box>
  );
};

// ─── Aggregation ─────────────────────────────────────────────────────────────

const aggregateByDate = (records) => {
  const map = {};
  records.forEach((r) => {
    const dateStr = getLocalDateOrFallback(r.utc_scanned);
    if (!dateStr) return;
    if (!map[dateStr]) map[dateStr] = { date: dateStr, green: 0, yellow: 0, red: 0 };
    map[dateStr].green += r.green ?? 0;
    map[dateStr].yellow += r.yellow ?? 0;
    map[dateStr].red += r.red ?? 0;
  });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
};

// ─── Main Component ──────────────────────────────────────────────────────────

/**
 * MissionChart
 * Props:
 *  - data          : array of records from API response (.data)
 *  - organizationId: number — visible for org 19 and 2
 *  - lang          : "dk" | "en"
 */
const MissionChart = ({
  data = [],
  organizationId,
  lang = "dk",
  hideFilters = false,
  externalFilters = null,
  height = 640,
}) => {
  const ALLOWED_ORGS = [19, 2];
  const isAllowed = ALLOWED_ORGS.includes(organizationId);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom());
  const [dateTo, setDateTo] = useState(defaultDateTo());
  const [timeRange, setTimeRange] = useState([0, 24]);
  const [setNumbers, setSetNumbers] = useState([]);
  const [showGreen, setShowGreen] = useState(true);
  const [showYellow, setShowYellow] = useState(true);
  const [showRed, setShowRed] = useState(true);

  const merged = externalFilters ?? {
    dateFrom,
    dateTo,
    timeRange,
    setNumbers,
    showGreen,
    showYellow,
    showRed,
    setDateFrom,
    setDateTo,
    setTimeRange,
    setSetNumbers,
    setShowGreen,
    setShowYellow,
    setShowRed,
  };

  const T = {
    title: lang === "dk" ? "Mission — Grøn / Gul / Rød" : "Mission — Green / Yellow / Red",
    timePeriod: lang === "dk" ? "Vælg tidsperiode:" : "Select time period:",
    timeOfDay: lang === "dk" ? "Vælg tidspunkt på døgnet:" : "Select time of day:",
    setNum: lang === "dk" ? "Sætnummer" : "Set number",
    downloadCsv: lang === "dk" ? "Download CSV" : "Download CSV",
    yAxis: lang === "dk" ? "Antal" : "Count",
    noData: lang === "dk" ? "Ingen data matcher de valgte filtre." : "No data matches the selected filters.",
    green: lang === "dk" ? "Grøn" : "Green",
    yellow: lang === "dk" ? "Gul" : "Yellow",
    red: lang === "dk" ? "Rød" : "Red",
  };

  const setNumberOptions = useMemo(() => {
    const raw = data
      .map((r) => r?.nb_set)
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== "");
    const uniq = Array.from(new Set(raw.map((v) => String(v))));
    uniq.sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return a.localeCompare(b, undefined, { numeric: true });
    });
    return uniq;
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      const dateStr = getLocalDateOrFallback(r.utc_scanned);
      if (merged.dateFrom && dateStr < merged.dateFrom) return false;
      if (merged.dateTo && dateStr > merged.dateTo) return false;
      if (merged.setNumbers.length > 0 && !merged.setNumbers.includes(String(r.nb_set))) return false;
      const hour = getLocalHourOrFallback(r.utc_scanned);
      if (hour !== null && (hour < merged.timeRange[0] || hour > merged.timeRange[1])) return false;
      return true;
    });
  }, [data, merged]);

  const chartData = useMemo(() => {
    // If current filters yield no data, show exactly one week of 0-values.
    if (!filtered.length) {
      const fallbackFrom = defaultDateFrom();
      const fallbackTo = defaultDateTo();
      const dateSeries = getDateRangeStringsInclusive(fallbackFrom, fallbackTo);
      return dateSeries.map((ds) => ({
        date: ds,
        label: fmtDateLabel(ds),
        green: 0,
        yellow: 0,
        red: 0,
      }));
    }

    // Otherwise, render only the dates that actually have values.
    const agg = aggregateByDate(filtered);
    return agg.map((r) => ({
      date: r.date,
      label: fmtDateLabel(r.date),
      green: merged.showGreen ? r.green ?? 0 : 0,
      yellow: merged.showYellow ? r.yellow ?? 0 : 0,
      red: merged.showRed ? r.red ?? 0 : 0,
    }));
  }, [filtered, merged.showGreen, merged.showYellow, merged.showRed]);

  // Totals for legend
  const totals = useMemo(() => ({
    green: chartData.reduce((s, r) => s + r.green, 0),
    yellow: chartData.reduce((s, r) => s + r.yellow, 0),
    red: chartData.reduce((s, r) => s + r.red, 0),
  }), [chartData]);


  const sliderMarks = Array.from({ length: 9 }, (_, i) => ({
    value: i * 3,
    label: String(i * 3),
  }));

  const legendFormatter = (value) => {
    if (value === "green") return `${T.green} ${totals.green}`;
    if (value === "yellow") return `${T.yellow} ${totals.yellow}`;
    if (value === "red") return `${T.red} ${totals.red}`;
    return value;
  };

  const summaryRows = useMemo(() => {
    const rows = [];
    if (merged.showGreen) rows.push({ label: T.green, value: totals.green, color: RISK_CHART_COLORS.green });
    if (merged.showYellow) rows.push({ label: T.yellow, value: totals.yellow, color: RISK_CHART_COLORS.yellow });
    if (merged.showRed) rows.push({ label: T.red, value: totals.red, color: RISK_CHART_COLORS.red });
    return rows;
  }, [
    merged.showGreen,
    merged.showYellow,
    merged.showRed,
    totals.green,
    totals.yellow,
    totals.red,
    T.green,
    T.yellow,
    T.red,
  ]);

  if (!isAllowed) return null;

  return (
    <Box sx={{ mt: 2 }}>
      {!hideFilters && (
        <>
          {/* ── Filters ── */}
          <div className="row g-3 mb-3" style={{ marginLeft: 0, marginRight: 0 }}>
            <div className="col-md-4">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {T.timePeriod}
              </Typography>
              <div className="d-flex gap-2">
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={merged.dateFrom}
                  inputProps={{ min: MIN_DATE, style: { fontSize: 13 } }}
                  onChange={(e) => merged.setDateFrom(e.target.value)}
                />
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={merged.dateTo}
                  inputProps={{ min: MIN_DATE, style: { fontSize: 13 } }}
                  onChange={(e) => merged.setDateTo(e.target.value)}
                />
              </div>
            </div>

            <div className="col-md-4 col-lg-3">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {T.setNum}
              </Typography>
              <MultiSelect
                label=""
                options={setNumberOptions}
                value={merged.setNumbers}
                onChange={(sel) => merged.setSetNumbers(sel)}
                onDelete={(v) => merged.setSetNumbers((p) => p.filter((x) => x !== v))}
              />
            </div>

            <div className="col-md-3 d-flex align-items-end gap-2">
              <Button
                variant="outlined"
                size="small"
                startIcon={<FaDownload />}
                onClick={() => downloadCSV(filtered)}
                sx={{
                  borderColor: RISK_CHART_COLORS.red,
                  color: RISK_CHART_COLORS.red,
                  "&:hover": { borderColor: "#922b21", color: "#922b21", background: "#fdf2f2" },
                  textTransform: "none",
                  fontSize: 13,
                  whiteSpace: "nowrap",
                }}
              >
                {T.downloadCsv}
              </Button>
            </div>
          </div>

          <div className="row g-3 mb-3 align-items-end" style={{ marginLeft: 0, marginRight: 0 }}>
            <div className="col-md-4">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {T.timeOfDay}
              </Typography>
              <Slider
                value={merged.timeRange}
                onChange={(_, v) => merged.setTimeRange(v)}
                valueLabelDisplay="on"
                min={0}
                max={24}
                marks={sliderMarks}
                sx={{
                  mt: 3,
                  color: "var(--org-bg-color, #f6615f)",
                  "& .MuiSlider-track": {
                    backgroundColor: "var(--org-bg-color, #f6615f)",
                    borderColor: "var(--org-bg-color, #f6615f)",
                  },
                  "& .MuiSlider-thumb": {
                    backgroundColor: "var(--org-bg-color, #f6615f)",
                  },
                  "& .MuiSlider-markActive": {
                    backgroundColor: "var(--org-bg-color, #f6615f)",
                  },
                  "& .MuiSlider-rail": {
                    backgroundColor: "var(--org-bg-tint-3, #ffefef)",
                    opacity: 1,
                  },
                  "& .MuiSlider-valueLabel": {
                    backgroundColor: "#fff",
                    color: "#000",
                    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.15)",
                  },
                }}
              />
            </div>

            {/* Checkboxes */}
            <div className="col-md-3 d-flex flex-column">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={merged.showGreen}
                    onChange={() => merged.setShowGreen((v) => !v)}
                    size="small"
                    sx={{ color: RISK_CHART_COLORS.green, "&.Mui-checked": { color: RISK_CHART_COLORS.green } }}
                  />
                }
                label={<Typography variant="body2">{T.green}</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={merged.showYellow}
                    onChange={() => merged.setShowYellow((v) => !v)}
                    size="small"
                    sx={{ color: RISK_CHART_COLORS.yellow, "&.Mui-checked": { color: RISK_CHART_COLORS.yellow } }}
                  />
                }
                label={<Typography variant="body2">{T.yellow}</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={merged.showRed}
                    onChange={() => merged.setShowRed((v) => !v)}
                    size="small"
                    sx={{ color: RISK_CHART_COLORS.red, "&.Mui-checked": { color: RISK_CHART_COLORS.red } }}
                  />
                }
                label={<Typography variant="body2">{T.red}</Typography>}
              />
            </div>
          </div>

          <hr style={{ borderColor: RISK_CHART_COLORS.red, margin: "12px 0 16px" }} />
        </>
      )}

      {/* ── Chart ── */}
      <Box sx={{ background: "#fff", borderRadius: 2, p: 0 }}>
        <div className="risk-chart-shell" style={{ width: "100%", height }}>
          <ChartSummaryRows rows={summaryRows} />
          <ResponsiveContainer width="100%" height="90%">
            <BarChart
              data={chartData}
              margin={{ top: 34, right: 30, left: 20, bottom: 70 }}
              barCategoryGap="25%"
              barGap={2}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 14, fontWeight: 600, fill: "#444" }}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={110}
                tickMargin={12}
              />
              <YAxis
                label={{
                  value: T.yAxis,
                  angle: -90,
                  position: "insideLeft",
                  offset: -4,
                  style: { fontSize: 12, fill: "#666" },
                }}
                tick={{ fontSize: 11, fill: "#666" }}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    showGreen={merged.showGreen}
                    showYellow={merged.showYellow}
                    showRed={merged.showRed}
                  />
                }
              />
              {merged.showGreen && (
                <Bar dataKey="green" name="green" fill={RISK_CHART_COLORS.green} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="green" position="top" style={{ fontSize: 14, fontWeight: 700, fill: "#1d1d1d" }} />
                </Bar>
              )}
              {merged.showYellow && (
                <Bar dataKey="yellow" name="yellow" fill={RISK_CHART_COLORS.yellow} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="yellow" position="top" style={{ fontSize: 14, fontWeight: 700, fill: "#1d1d1d" }} />
                </Bar>
              )}
              {merged.showRed && (
                <Bar dataKey="red" name="red" fill={RISK_CHART_COLORS.red} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="red" position="top" style={{ fontSize: 14, fontWeight: 700, fill: "#1d1d1d" }} />
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Box>

      <hr style={{ borderColor: RISK_CHART_COLORS.red, margin: "16px 0" }} />
    </Box>
  );
};

export default MissionChart;
